import type { QueryClient } from "@tanstack/react-query";
import {
  Outlet,
  createRootRouteWithContext,
  createRoute,
  createRouter,
  lazyRouteComponent,
  redirect,
  type AnyRoute,
} from "@tanstack/react-router";
import { supabase } from "./lib/supabase";
import { meQuery } from "./lib/session";
import type { PageModule } from "./lib/page";
import { LoginPage } from "./routes/login";
import { ForgotPasswordPage } from "./routes/forgot-password";
import { NotFound, RouteError, PendingScreen } from "./routes/misc";
import { WorkspaceLayout, loadWorkspace } from "./routes/w/layout";

type Ctx = { queryClient: QueryClient };

// Search params stay plain strings, as in the old app (no JSON parsing of "2026").
function parseSearch(str: string) {
  return Object.fromEntries(new URLSearchParams(str).entries());
}
function stringifySearch(search: Record<string, unknown>) {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(search)) if (v !== undefined && v !== null && v !== "") p.set(k, String(v));
  const s = p.toString();
  return s ? `?${s}` : "";
}

const rootRoute = createRootRouteWithContext<Ctx>()({
  component: Outlet,
  errorComponent: RouteError,
  notFoundComponent: NotFound,
});

const loginRoute = createRoute({ getParentRoute: () => rootRoute, path: "/login", component: LoginPage });
const forgotPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/forgot-password",
  component: ForgotPasswordPage,
});

// Everything below needs a session. The profile is loaded once and then served from cache.
const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "app",
  beforeLoad: async ({ context, location }) => {
    const { data } = await supabase().auth.getSession();
    if (!data.session) throw redirect({ to: "/login", search: { next: location.href } });
    const me = await context.queryClient.ensureQueryData(meQuery);
    if (me.profile.must_change_password && location.pathname !== "/change-password") {
      throw redirect({ to: "/change-password" });
    }
    return { me };
  },
  component: Outlet,
});

/** A lazily loaded screen: its code and data load together when the route is entered. */
function page(parent: AnyRoute, path: string, importer: () => Promise<PageModule>) {
  return createRoute({
    getParentRoute: () => parent,
    path,
    loaderDeps: ({ search }) => ({ search: search as Record<string, string> }),
    loader: async ({ context, params, deps }) => {
      const m = await importer();
      await m.load?.({
        queryClient: (context as Ctx).queryClient,
        params: params as Record<string, string>,
        search: deps.search,
      });
    },
    component: lazyRouteComponent(importer, "Page"),
  });
}

const indexRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/",
  beforeLoad: ({ context }) => {
    const first = context.me.workspaces[0];
    if (context.me.profile.is_sysadmin && !first) throw redirect({ to: "/admin" });
    if (first) throw redirect({ to: `/w/${first.slug}` });
  },
  component: lazyRouteComponent(() => import("./routes/no-workspace"), "Page"),
});

const changePassword = page(appRoute, "/change-password", () => import("./routes/change-password"));

const adminRoute = createRoute({ getParentRoute: () => appRoute, path: "/admin", component: Outlet });
const admin = [
  page(adminRoute, "/", () => import("./routes/admin/index")),
  page(adminRoute, "/users", () => import("./routes/admin/users")),
  page(adminRoute, "/users/$id", () => import("./routes/admin/user")),
  page(adminRoute, "/templates", () => import("./routes/admin/templates")),
  page(adminRoute, "/templates/series", () => import("./routes/admin/series-templates")),
];

const wRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/w/$slug",
  loader: ({ context, params }) => loadWorkspace(context.queryClient, params.slug),
  component: WorkspaceLayout,
});
const w = (path: string, importer: () => Promise<PageModule>) => page(wRoute, path, importer);
const workspace = [
  w("/", () => import("./routes/w/home")),
  w("/train", () => import("./routes/w/train")),
  w("/sessions/$id", () => import("./routes/w/session")),
  w("/sessions/$id/log", () => import("./routes/w/session-log")),
  w("/calendar", () => import("./routes/w/calendar")),
  w("/schedule", () => import("./routes/w/schedule")),
  w("/plans/$id", () => import("./routes/w/plan")),
  w("/exercises", () => import("./routes/w/exercises")),
  w("/exercises/new", () => import("./routes/w/exercise-new")),
  w("/exercises/$id", () => import("./routes/w/exercise")),
  w("/exercises/$id/edit", () => import("./routes/w/exercise-edit")),
  w("/series", () => import("./routes/w/series")),
  w("/series/$id", () => import("./routes/w/series-edit")),
  w("/series/templates", () => import("./routes/w/series-templates")),
  w("/series/templates/$id", () => import("./routes/w/series-template")),
  w("/programs", () => import("./routes/w/programs")),
  w("/programs/$id", () => import("./routes/w/program")),
  w("/programs/templates", () => import("./routes/w/program-templates")),
  w("/programs/templates/$id", () => import("./routes/w/program-template")),
  w("/nutrition", () => import("./routes/w/nutrition")),
  w("/nutrition/targets", () => import("./routes/w/nutrition-targets")),
  w("/check-ins", () => import("./routes/w/check-ins")),
  w("/check-ins/$id", () => import("./routes/w/check-in")),
  w("/journal", () => import("./routes/w/journal")),
  w("/history", () => import("./routes/w/history")),
  w("/me", () => import("./routes/w/me")),
  w("/clients", () => import("./routes/w/clients")),
  w("/clients/new", () => import("./routes/w/client-new")),
  w("/clients/$id", () => import("./routes/w/client")),
  w("/clients/$id/progress", () => import("./routes/w/client-progress")),
  w("/team", () => import("./routes/w/team")),
  w("/team/$id", () => import("./routes/w/team-member")),
  w("/settings", () => import("./routes/w/settings")),
];

const routeTree = rootRoute.addChildren([
  loginRoute,
  forgotPasswordRoute,
  appRoute.addChildren([
    indexRoute,
    changePassword,
    adminRoute.addChildren(admin),
    wRoute.addChildren(workspace),
  ]),
]);

export function makeRouter(queryClient: QueryClient) {
  return createRouter({
    routeTree,
    context: { queryClient },
    parseSearch,
    stringifySearch,
    // Touching a link starts loading the next screen's code and data.
    defaultPreload: "intent",
    // Data freshness is TanStack Query's job, not the router's.
    defaultPreloadStaleTime: 0,
    // Show a spinner only when a screen takes noticeably long.
    defaultPendingMs: 400,
    defaultPendingComponent: PendingScreen,
    defaultNotFoundComponent: NotFound,
    scrollRestoration: true,
  });
}
